import { el } from '../dom.js';
import type { I18n } from '../i18n.js';
import type { OnboardingResult, OnboardingTopRole } from '../session.js';
import { api } from '../session.js';

/**
 * §9a.1 role-onboarding questionnaire. Runs between the landing consent and
 * the first scenario. Answers drive a role-affinity calculation on the server;
 * the UI then shows the top-3..5 roles with an override option.
 *
 * GDPR Art.9 special-category data (religiosity, political self-identification)
 * is gated behind an explicit consent checkbox. Without that consent the two
 * sensitive questions are not rendered and the server refuses to accept values
 * for them.
 */

export interface OnboardingProps {
  i18n: I18n;
  /** Invoked with the final confirmed roleId after /confirm succeeds. */
  onConfirmed: (roleId: string) => void;
  onError: (err: unknown) => void;
}

interface Question {
  id: string;
  kind: 'single' | 'multi';
  options: readonly string[];
  sensitive?: boolean;
  /** Maximum picks for a `multi` question. */
  maxPicks?: number;
}

const QUESTIONS: readonly Question[] = [
  {
    id: 'income',
    kind: 'single',
    options: ['wages_taxed', 'self_employed', 'benefits', 'pension', 'mixed', 'none', 'prefer_not_to_say'],
  },
  {
    id: 'family',
    kind: 'single',
    options: ['parent_minor', 'caregiver_elder', 'foster_parent', 'single_no_deps', 'partner_no_deps', 'adult_child', 'prefer_not_to_say'],
  },
  {
    id: 'aid_experience',
    kind: 'single',
    options: ['receiving_now', 'past_recipient', 'family_receiving', 'never', 'prefer_not_to_say'],
  },
  {
    id: 'neighbourhood',
    kind: 'single',
    options: ['rural', 'small_town', 'suburban', 'urban_metro', 'displaced_camp', 'prefer_not_to_say'],
  },
  {
    id: 'profession',
    kind: 'multi',
    maxPicks: 5,
    options: [
      'medicine', 'education', 'law_enforcement', 'judiciary_legal',
      'economics_analyst', 'journalism', 'ngo_social_work', 'civil_service',
      'clergy', 'business', 'tech', 'environment', 'other', 'prefer_not_to_say',
    ],
  },
  {
    id: 'religiosity',
    kind: 'single',
    sensitive: true,
    options: ['high_practicing', 'moderate', 'cultural_only', 'non_religious', 'prefer_not_to_say'],
  },
  {
    id: 'politics',
    kind: 'single',
    sensitive: true,
    options: ['libertarian', 'conservative', 'centrist', 'social_democrat', 'left_progressive', 'indigenous_first', 'diaspora_first', 'prefer_not_to_say'],
  },
];

export function OnboardingView({ i18n, onConfirmed, onError }: OnboardingProps): HTMLElement {
  const root = el('section', { 'aria-label': 'Onboarding', class: 'stack-lg' });
  renderQuestionnaire();
  return root;

  // ------- questionnaire step -------
  function renderQuestionnaire(): void {
    const answers: Record<string, string | string[]> = {};
    const status = el('p', { class: 'hero-lede', role: 'status', 'aria-live': 'polite' }, '');

    // Non-sensitive questions always visible.
    const basic = el('div', { class: 'onboarding-questions' });
    for (const q of QUESTIONS.filter((x) => !x.sensitive)) {
      basic.appendChild(renderQuestion(q, answers));
    }

    // Sensitive questions gated by explicit Art.9 consent.
    const art9Checkbox = el('input', { type: 'checkbox', id: 'art9-consent' }) as HTMLInputElement;
    const sensitiveWrap = el('div', { class: 'onboarding-sensitive', hidden: true }) as HTMLDivElement;
    for (const q of QUESTIONS.filter((x) => x.sensitive)) {
      sensitiveWrap.appendChild(renderQuestion(q, answers));
    }
    art9Checkbox.addEventListener('change', () => {
      sensitiveWrap.hidden = !art9Checkbox.checked;
      if (!art9Checkbox.checked) {
        // Purge sensitive answers if consent is withdrawn before submit.
        for (const q of QUESTIONS.filter((x) => x.sensitive)) delete answers[q.id];
        sensitiveWrap.querySelectorAll('select').forEach((s) => {
          (s as HTMLSelectElement).value = '';
        });
      }
    });

    const art9Block = el('details', { class: 'panel panel-collapsible onboarding-art9' }, [
      el('summary', {}, i18n.t('onboarding.art9_consent_heading')),
      el('p', { class: 'onboarding-art9-note' }, i18n.t('onboarding.art9_consent_note')),
      el('label', { class: 'consent', for: 'art9-consent' }, [
        art9Checkbox,
        el('span', {}, i18n.t('onboarding.art9_consent_label')),
      ]),
      sensitiveWrap,
    ]);

    const submit = el('button', { class: 'block', type: 'button' }, i18n.t('onboarding.submit')) as HTMLButtonElement;
    const veilShortcut = el(
      'button',
      { class: 'ghost', type: 'button' },
      i18n.t('onboarding.veil_cta'),
    ) as HTMLButtonElement;

    submit.addEventListener('click', async () => {
      if (!validateRequired(answers, status)) return;
      submit.disabled = true;
      veilShortcut.disabled = true;
      try {
        const result = await api.submitOnboarding(answers, art9Checkbox.checked);
        renderReview(result);
      } catch (err) {
        submit.disabled = false;
        veilShortcut.disabled = false;
        onError(err);
      }
    });

    veilShortcut.addEventListener('click', async () => {
      // Explicit R0 opt-in: still submit the form (empty answers), then confirm
      // the veil. This keeps the server-side `roleProfile` non-empty and
      // consistent for analytics (§9a.1 captures that the player chose veil
      // as opposed to never starting onboarding).
      submit.disabled = true;
      veilShortcut.disabled = true;
      try {
        await api.submitOnboarding({}, false);
        const confirmed = await api.confirmOnboardingRole('R0');
        onConfirmed(confirmed.roleId);
      } catch (err) {
        submit.disabled = false;
        veilShortcut.disabled = false;
        onError(err);
      }
    });

    root.replaceChildren(
      el('header', {}, [
        el('h1', { class: 'hero-title' }, i18n.t('onboarding.heading')),
        el('p', { class: 'hero-lede' }, i18n.t('onboarding.intro')),
      ]),
      basic,
      art9Block,
      el('div', { class: 'actions' }, [
        el('div', { class: 'actions-primary' }, [submit]),
        el('div', { class: 'actions-secondary' }, [veilShortcut]),
      ]),
      status,
    );
  }

  function renderQuestion(q: Question, sink: Record<string, string | string[]>): HTMLElement {
    const selectId = `onb-${q.id}`;
    if (q.kind === 'single') {
      const select = el('select', { id: selectId, name: q.id }) as HTMLSelectElement;
      select.appendChild(el('option', { value: '' }, i18n.t('survey.choose')));
      for (const opt of q.options) {
        select.appendChild(el('option', { value: opt }, i18n.t(`onboarding.q.${q.id}.${opt}`)));
      }
      select.addEventListener('change', () => {
        if (select.value) sink[q.id] = select.value;
        else delete sink[q.id];
      });
      return el('label', { class: 'survey-q', for: selectId }, [
        el('span', {}, i18n.t(`onboarding.q.${q.id}.label`)),
        select,
      ]);
    }
    // multi
    const wrap = el('fieldset', { class: 'onboarding-multi' });
    const maxPicks = q.maxPicks;
    const legendText = i18n.t(`onboarding.q.${q.id}.label`);
    wrap.appendChild(el('legend', { class: 'onboarding-multi-legend' }, legendText));

    const counter = maxPicks
      ? (el('span', {
          class: 'onboarding-multi-counter',
          'aria-live': 'polite',
        }, `0 / ${maxPicks}`) as HTMLElement)
      : null;
    if (counter) {
      wrap.appendChild(el('p', { class: 'onboarding-multi-hint' }, [
        el('span', {}, i18n.t('onboarding.multi.hint')),
        counter,
      ]));
    }

    const chipsWrap = el('div', { class: 'onboarding-chips', role: 'group' });
    wrap.appendChild(chipsWrap);

    const picks = new Set<string>();
    const chipRefs: Array<{ opt: string; chip: HTMLLabelElement; cb: HTMLInputElement }> = [];

    const refreshState = () => {
      const atLimit = maxPicks !== undefined && picks.size >= maxPicks;
      const pnsActive = picks.has('prefer_not_to_say');
      for (const { opt, chip, cb } of chipRefs) {
        const isPicked = picks.has(opt);
        chip.classList.toggle('is-selected', isPicked);
        // Disable unselected non-PNS chips when at the limit, and disable all
        // real chips when PNS is active (or vice-versa: disable PNS chip when
        // real picks exist).
        let disabled = false;
        if (pnsActive && opt !== 'prefer_not_to_say') disabled = true;
        else if (!pnsActive && picks.size > 0 && opt === 'prefer_not_to_say') disabled = true;
        else if (atLimit && !isPicked && opt !== 'prefer_not_to_say') disabled = true;
        cb.disabled = disabled;
        chip.classList.toggle('is-disabled', disabled);
        chip.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }
      if (counter && maxPicks !== undefined) {
        counter.textContent = `${picks.size} / ${maxPicks}`;
        counter.classList.toggle('is-full', atLimit);
      }
    };

    for (const opt of q.options) {
      const cbId = `${selectId}-${opt}`;
      const cb = el('input', {
        type: 'checkbox',
        id: cbId,
        value: opt,
        name: q.id,
        class: 'onboarding-chip-input',
      }) as HTMLInputElement;
      const chip = el('label', { class: 'onboarding-chip', for: cbId }, [
        cb,
        el('span', { class: 'onboarding-chip-label' }, i18n.t(`onboarding.q.${q.id}.${opt}`)),
      ]) as HTMLLabelElement;
      cb.addEventListener('change', () => {
        if (cb.checked) {
          if (opt === 'prefer_not_to_say') {
            // PNS is mutually exclusive with real picks.
            picks.clear();
            for (const ref of chipRefs) {
              if (ref.opt !== 'prefer_not_to_say') ref.cb.checked = false;
            }
            picks.add(opt);
          } else {
            // Clear PNS if a real pick comes in.
            const pnsRef = chipRefs.find((r) => r.opt === 'prefer_not_to_say');
            if (pnsRef?.cb.checked) {
              pnsRef.cb.checked = false;
              picks.delete('prefer_not_to_say');
            }
            if (maxPicks !== undefined && picks.size >= maxPicks) {
              // Guard: shouldn't fire because chip is disabled, but stay safe.
              cb.checked = false;
              return;
            }
            picks.add(opt);
          }
        } else {
          picks.delete(opt);
        }
        if (picks.size > 0) sink[q.id] = [...picks];
        else delete sink[q.id];
        refreshState();
      });
      chipRefs.push({ opt, chip, cb });
      chipsWrap.appendChild(chip);
    }

    refreshState();
    return wrap;
  }

  function validateRequired(
    answers: Record<string, string | string[]>,
    status: HTMLElement,
  ): boolean {
    // Only the five non-sensitive questions are required.
    const required = QUESTIONS.filter((q) => !q.sensitive).map((q) => q.id);
    for (const id of required) {
      const v = answers[id];
      const filled = Array.isArray(v) ? v.length > 0 : typeof v === 'string' && v.length > 0;
      if (!filled) {
        status.textContent = i18n.t('onboarding.incomplete');
        return false;
      }
    }
    status.textContent = '';
    return true;
  }

  // ------- review step: top roles + override -------
  function renderReview(result: OnboardingResult): void {
    const top = result.topRoles;
    if (top.length === 0) {
      // No signal — jump straight to veil.
      void (async () => {
        try {
          const c = await api.confirmOnboardingRole('R0');
          onConfirmed(c.roleId);
        } catch (err) {
          onError(err);
        }
      })();
      return;
    }

    const topList = el('ol', { class: 'onboarding-top-roles' });
    for (const r of top) {
      topList.appendChild(renderTopRole(r));
    }

    const acceptTop = el(
      'button',
      { class: 'block', type: 'button' },
      i18n.t('onboarding.top_role_cta', { role: i18n.t(top[0]!.labelKey) }),
    ) as HTMLButtonElement;
    acceptTop.addEventListener('click', () => pick(top[0]!.roleId));

    // Manual override: select from all 32.
    const manualSelect = el('select', {
      id: 'onb-manual',
      'aria-label': i18n.t('onboarding.manual_cta'),
    }) as HTMLSelectElement;
    manualSelect.appendChild(el('option', { value: '' }, i18n.t('survey.choose')));
    for (const r of result.allRoles) {
      manualSelect.appendChild(el('option', { value: r.roleId }, i18n.t(r.labelKey)));
    }
    const manualBtn = el(
      'button',
      { class: 'ghost', type: 'button', disabled: true },
      i18n.t('onboarding.manual_confirm'),
    ) as HTMLButtonElement;
    manualSelect.addEventListener('change', () => {
      manualBtn.disabled = !manualSelect.value;
    });
    manualBtn.addEventListener('click', () => {
      if (manualSelect.value) pick(manualSelect.value);
    });

    const veilBtn = el(
      'button',
      { class: 'ghost', type: 'button' },
      i18n.t('onboarding.veil_cta'),
    ) as HTMLButtonElement;
    veilBtn.addEventListener('click', () => pick('R0'));

    root.replaceChildren(
      el('header', {}, [
        el('h1', { class: 'hero-title' }, i18n.t('onboarding.review_heading')),
        el('p', { class: 'hero-lede' }, i18n.t('onboarding.review_intro')),
      ]),
      topList,
      el('div', { class: 'actions' }, [
        el('div', { class: 'actions-primary' }, [acceptTop]),
      ]),
      el('details', { class: 'panel panel-collapsible' }, [
        el('summary', {}, i18n.t('onboarding.manual_cta')),
        el('label', { class: 'survey-q', for: 'onb-manual' }, [
          el('span', {}, i18n.t('onboarding.manual_help')),
          manualSelect,
        ]),
        manualBtn,
      ]),
      el('div', { class: 'actions' }, [
        el('div', { class: 'actions-secondary' }, [veilBtn]),
      ]),
    );

    async function pick(roleId: string): Promise<void> {
      acceptTop.disabled = true;
      veilBtn.disabled = true;
      manualBtn.disabled = true;
      try {
        const c = await api.confirmOnboardingRole(roleId);
        onConfirmed(c.roleId);
      } catch (err) {
        acceptTop.disabled = false;
        veilBtn.disabled = false;
        manualBtn.disabled = !manualSelect.value;
        onError(err);
      }
    }
  }

  function renderTopRole(r: OnboardingTopRole): HTMLElement {
    const pct = Math.round(r.score * 100);
    const bar = el('div', { class: 'onboarding-affinity-bar' }, [
      el('span', {
        class: 'onboarding-affinity-fill',
        style: `inline-size:${pct}%`,
      }),
    ]);
    return el('li', { class: 'onboarding-top-role' }, [
      el('div', { class: 'onboarding-top-role-head' }, [
        el('strong', {}, i18n.t(r.labelKey)),
        el('span', { class: 'onboarding-affinity-pct' }, `${pct}%`),
      ]),
      el('p', { class: 'card-sub' }, i18n.t(r.descriptionKey)),
      bar,
    ]);
  }
}
